/**
 * `TimelineScrubber` / `TimelineScrubberView` — bottom-bar 4D scrubber.
 *
 * WP-11 design-system compliance sweep:
 *   - Removed every dependency on the legacy `.timeline-scrubber` and
 *     `.timeline-meta` global CSS classes that lived in
 *     `styles/global.css`. The component now resolves every visual
 *     property through `tokens.*` and renders entirely via the
 *     design-bridge primitives (`Slider`, `Stack`, `Text`).
 *   - Live / replay buttons honour the 40 px touch target floor and
 *     the visible `:focus-visible` ring per `DESIGN_POLICY.md` §5.
 *   - The mono timestamp readouts use `tokens.font.role.engMono` so
 *     IDs and timestamps stay aligned with the rest of the
 *     engineering canvases.
 *
 * The component still owns:
 *   - The headless `<TimelineScrubberView>` (consumed by the WP-8
 *     Sensor / live overlay ToolCard via `useViewerSlice`),
 *   - The store-bound `<TimelineScrubber>` (bottom bar of AppShell).
 */

import { useMemo } from 'react';
import { Slider, Stack, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { deriveTimelineWindow, formatTimelineLabel } from './timeWindow';

export interface TimelineScrubberViewProps {
  /** Lower bound of the timeline window in epoch milliseconds. */
  readonly minMs: number;
  /** Upper bound of the timeline window in epoch milliseconds. */
  readonly maxMs: number;
  /**
   * Active time as a Date or `null` when the scrubber is following
   * "live" (right-edge of the window).
   */
  readonly time: Date | null;
  /** Step size for the slider input, in milliseconds. */
  readonly stepMs?: number;
  /**
   * Optional override label for the right-edge "Live" button. Used by
   * the SensorOverlayToolCard to show "Now" while replaying.
   */
  readonly liveLabel?: string;
  /**
   * Optional title; defaults to "Timeline". Card consumers use this
   * to render the section heading inline with their own chrome.
   */
  readonly title?: string;
  /** Test id for Playwright / RTL specs. */
  readonly 'data-testid'?: string;

  readonly onChange: (next: Date) => void;
  readonly onLive: () => void;
}

const DEFAULT_STEP_MS = 60 * 1000;

/**
 * Headless scrubber view — token-driven, primitive-based render of the
 * timeline window + live button. Accepts the window and time via
 * props instead of pulling from `useViewerStore`, so it can be reused
 * by ToolCards.
 */
export function TimelineScrubberView({
  minMs,
  maxMs,
  time,
  stepMs = DEFAULT_STEP_MS,
  liveLabel = 'Live',
  title = 'Timeline',
  'data-testid': testId,
  onChange,
  onLive,
}: TimelineScrubberViewProps) {
  const sliderValue = time ? time.getTime() : maxMs;
  const clamped = Math.max(minMs, Math.min(maxMs, sliderValue));
  const monoStyle = {
    fontFamily: tokens.font.family.mono,
    fontSize: tokens.font.size.xs,
    color: tokens.color.ink.secondary,
  } as const;

  return (
    <section
      aria-label={`${title} controls`}
      data-testid={testId}
      style={{
        display: 'grid',
        gap: tokens.space[1],
        padding: `0 ${tokens.space[2]}px`,
      }}
    >
      <Stack
        direction="row"
        align="center"
        justify="space-between"
        gap={2}
      >
        <h3
          style={{
            margin: 0,
            fontSize: tokens.font.size.xs,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </h3>
        <small style={monoStyle}>
          {time ? formatTimelineLabel(time.getTime()) : liveLabel}
        </small>
      </Stack>
      <Slider
        min={minMs}
        max={maxMs}
        step={stepMs}
        value={clamped}
        aria-label={`${title} scrubber`}
        onChange={(next) => onChange(new Date(next))}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: tokens.space[2],
        }}
      >
        <small style={monoStyle}>{formatTimelineLabel(minMs)}</small>
        <button
          type="button"
          onClick={onLive}
          style={{
            minHeight: tokens.density.touchMin,
            paddingInline: tokens.space[3],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[2],
            color: tokens.color.ink.primary,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.xs,
            cursor: 'pointer',
          }}
        >
          {liveLabel}
        </button>
        <small style={{ ...monoStyle, textAlign: 'right' }}>
          {formatTimelineLabel(maxMs)}
        </small>
      </div>
    </section>
  );
}

/**
 * Bottom-bar TimelineScrubber wired to `useViewerStore`. Internally
 * delegates to {@link TimelineScrubberView}.
 */
export function TimelineScrubber() {
  const catalog = useViewerStore((s) => s.catalog);
  const visible = useViewerStore((s) => s.visibleLayerIds);
  const time = useViewerStore((s) => s.time);
  const setTime = useViewerStore((s) => s.setTime);

  const window = useMemo(
    () => deriveTimelineWindow(catalog, visible),
    [catalog, visible],
  );

  return (
    <TimelineScrubberView
      minMs={window.minMs}
      maxMs={window.maxMs}
      time={time}
      onChange={(next) => setTime(next)}
      onLive={() => setTime(null)}
    />
  );
}
